import { useEffect, useLayoutEffect, useRef, useState } from "react"
import SignatureCanvas from "react-signature-canvas"
import { Checkbox, FormControlLabel } from "@mui/material"
import { FaTimes } from "react-icons/fa"
import Input from "../Input/Input"
import { generateSignatureImage } from "./signatureImage"

// The signing dialog of the public signing pages in intent mode (WS#2211755): draw, or "Use my
// typed name". Full-screen sheet on phones, centered window from 640px, the full consent text, and
// a Sign button that is never disabled: tapping it with something missing says what is missing next
// to that field and moves there. Answers { signatureURL, typedName, consent }.

const PAD_HEIGHT = 180
const SIGNATURE_PAPER = '#FFFFFF'

const errorClass = 'mt-1 text-sm text-primary-static-error-600'
const secondaryButtonClass = 'h-11 px-4 rounded-lg border border-[var(--color-gray-400)] text-md font-semibold text-text-primary hover:bg-primary-main/10 hover:border-primary-main'

export function SignIntentDialog({ onConfirm, onClose, consentText, consentVersion }) {
  const [typedName, setTypedName] = useState('')
  const [hasDrawing, setHasDrawing] = useState(false)
  const [typedSignatureURL, setTypedSignatureURL] = useState(null)
  const [consented, setConsented] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [padWidth, setPadWidth] = useState(300)

  const sigRef = useRef(null)
  const padBoxRef = useRef(null)
  const nameRef = useRef(null)
  const consentRef = useRef(null)
  const signingRef = useRef(false)
  const latestTypedName = useRef(typedName)
  latestTypedName.current = typedName
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  const trimmedName = typedName.trim()
  const missing = {
    name: !trimmedName,
    signature: !hasDrawing && !typedSignatureURL,
    consent: !consented,
  }

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onCloseRef.current?.()
    }
    document.addEventListener('keydown', onKeyDown)
    const { overflow } = document.body.style
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = overflow
    }
  }, [])

  // signature_pad maps touches in CSS pixels, so the canvas must be exactly as wide as its box.
  // A resize clears the canvas, so the drawing is gone too.
  const padShown = !typedSignatureURL
  useLayoutEffect(() => {
    if (!padShown) return undefined
    const measure = () => {
      const width = padBoxRef.current?.clientWidth
      if (width) setPadWidth(width)
      setHasDrawing(false)
    }
    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [padShown])

  const changeName = (value) => {
    setTypedName(value)
    setTypedSignatureURL(null)
  }

  const applyTypedName = async () => {
    if (!trimmedName) {
      setSubmitted(true)
      nameRef.current?.focus()
      return
    }
    const renderedName = typedName
    const generated = await generateSignatureImage(trimmedName)
    if (latestTypedName.current !== renderedName) return
    setHasDrawing(false)
    setTypedSignatureURL(generated)
  }

  const clearSignature = () => {
    sigRef.current?.clear()
    setHasDrawing(false)
    setTypedSignatureURL(null)
  }

  const sign = () => {
    if (signingRef.current) return
    setSubmitted(true)
    if (missing.name) {
      nameRef.current?.focus()
      return
    }
    if (missing.signature) {
      padBoxRef.current?.scrollIntoView?.({ block: 'center' })
      return
    }
    if (missing.consent) {
      consentRef.current?.focus()
      consentRef.current?.scrollIntoView?.({ block: 'center' })
      return
    }
    signingRef.current = true
    onConfirm({
      signatureURL: typedSignatureURL || sigRef.current.getTrimmedCanvas().toDataURL(),
      typedName: trimmedName,
      consent: { accepted: true, version: consentVersion },
    })
  }

  return (
    <div className="fixed inset-0 z-[10000] flex sm:items-center sm:justify-center sm:p-4" style={{ backgroundColor: 'rgba(0, 0, 0, 0.4)' }}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="sign-intent-title"
        className="flex flex-col w-full h-[100dvh] sm:h-auto sm:max-h-[calc(100dvh-32px)] sm:max-w-[480px] sm:rounded-2xl bg-paper text-text-primary font-sans shadow-xl overflow-hidden"
      >
        <div className="flex items-center justify-between shrink-0 h-14 pl-4 pr-1 border-b border-divider">
          <h2 id="sign-intent-title" className="text-lg font-semibold">Add your signature</h2>
          <button type="button" aria-label="Close" className="w-11 h-11 flex items-center justify-center text-text-secondary" onClick={() => onClose()}>
            <FaTimes size={18} />
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4 flex flex-col gap-4">
          <Input
            label="Full legal name"
            placeholder="Type your full legal name"
            value={typedName}
            onChange={(e) => changeName(e.target.value)}
            autoComplete="name"
            errorMsg={submitted && missing.name ? 'Enter your full legal name' : undefined}
            inputRef={nameRef}
            width="w-full"
          />

          <div>
            <div ref={padBoxRef} className="rounded-lg border border-divider overflow-hidden" style={{ backgroundColor: SIGNATURE_PAPER }}>
              {typedSignatureURL ? (
                <div className="flex items-center justify-center px-4" style={{ height: PAD_HEIGHT }}>
                  <img src={typedSignatureURL} alt="Your typed-name signature" className="max-w-full" style={{ maxHeight: 72 }} />
                </div>
              ) : (
                <SignatureCanvas
                  ref={sigRef}
                  velocityFilterWeight={1}
                  onEnd={() => setHasDrawing(!sigRef.current?.isEmpty())}
                  canvasProps={{ width: padWidth, height: PAD_HEIGHT, className: 'sigCanvas block', 'aria-label': 'Signature pad' }}
                />
              )}
            </div>
            <div className="flex items-center justify-between gap-3 mt-2">
              <button type="button" className={secondaryButtonClass} onClick={applyTypedName}>
                Use my typed name
              </button>
              <button type="button" className="h-11 px-3 text-md font-semibold text-primary-main" onClick={clearSignature}>
                Clear
              </button>
            </div>
            {submitted && missing.signature && (
              <p role="alert" className={errorClass}>Draw your signature, or tap "Use my typed name"</p>
            )}
          </div>

          <div>
            <FormControlLabel
              sx={{ alignItems: 'flex-start', mr: 0, ml: -1 }}
              control={(
                <Checkbox
                  sx={{ mt: -1 }}
                  checked={consented}
                  inputRef={consentRef}
                  onChange={(e) => setConsented(e.target.checked)}
                />
              )}
              label={consentText}
              slotProps={{ typography: { sx: { fontSize: 14, lineHeight: 1.5, overflowWrap: 'anywhere' } } }}
            />
            {submitted && missing.consent && (
              <p role="alert" className={errorClass}>Tick the box to agree to sign electronically</p>
            )}
          </div>
        </div>

        <div className="shrink-0 px-4 pt-3 pb-[max(12px,env(safe-area-inset-bottom))] border-t border-divider">
          <button
            type="button"
            className="w-full h-12 rounded-lg text-md font-semibold bg-primary-main text-primary-contrast hover:bg-primary-main/90"
            onClick={sign}
          >
            Sign
          </button>
        </div>
      </div>
    </div>
  )
}
